import React, { useState } from 'react';
import { useFutureOrderStore } from '@components/Stores/FutureOrdersStore';
import { useMaterialsStore } from '@components/Stores/MaterialsStore';
import { useAccountStore } from '@components/Stores/accountStore';
import { useFutureOrders } from '@hooks/useFutureOrders';
import { useInventory } from '@hooks/useInventory';
import { useInventoryReports } from '@hooks/useInventoryReports';
import { useNavigate } from 'react-router-dom';
import { triggerExposureCalc, useExposure } from '../../hooks/useExposure';
import { getNetExposure } from '../../utils/exposureUtils';
import { balanceMaterial } from '../BalanceFlow/balanceUtils';
import { aggregateFutureOrdersToMaterialsDic, dictionaryToStackChart } from '../FutureOrder/FutureOrders/Utils';
import { ExposureCard } from './ExposureCard';
import { NotificationDashboardCard } from './dashboard-components/DashboardNotifications/NotificationDashboardCard';
import { SkeletonDashboard } from './SkeletonDashboard';
import { StackedBarDoughnutChartCard } from './StackedBarDoughnutChartCard';
import { InventoryChartCard } from './dashboard-components/InventoryChartCard';
import { getExposureCardData, getMaterialsCardData, getMaterialToAccountById, mapPositions } from './dashboardUtils';
import { MaterialTrendButton } from './MaterialTrendButton';
import { GENERAL_ID, useExposureStore } from '../../components/Stores/exposureDataStore';
import {
  ADD_FO_FIRST_STEP,
  ADD_FUTURE_ORDER_DEFAULT_ORDER_ID,
  ADD_FUTURE_ORDER_ROOT_ROUTE,
} from '../FutureOrder/AddNewFutureOrder/AddNewFutureOrderUtils';
import { useFinancialPositions } from '@hooks/useFinancialPositions';
import { useFinancialPositionsStore } from '../../components/Stores/FinancialPositionStore';
import { ExposureDetailsPopup } from './dashboard-components/ExposureDetailsPopup';
import { useCurrencyRates } from '@hooks/useCurrencyRates';
import { DashboardNotification } from '../../sp-types/notificationType';
import { getNotifications } from '../../sp-firebase/notificationsFB';
import { OverlayDisplayDiv } from '../../components/UI-Elements/PopoverOverlays/OverlayDisplayDiv';
import { StackedBarChartDetails } from './StackedBarChartDetails';
import { useUser } from '@hooks/useUser';
import { InventoryDetails } from './dashboard-components/InventoryDetails';
import { AddSalesCommitmentPopup } from '../FutureOrder/SalesCommComponents/AddSalesCommitmentPopup';

export default function Dashboard() {
  const { account, materialColorDictionary: colorsMap } = useAccountStore();
  const { data: user } = useUser();

  const { data: inventoryData, refetch: fetchOldInventory } = useInventory();

  const { refetch: fetchFinPositions } = useFinancialPositions();
  const { financialPositions } = useFinancialPositionsStore();

  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear() || 2026);
  const { data: inventoryReports, refetch: fetchInventory } = useInventoryReports();

  const [editable, setEditable] = useState<boolean>(!!user?.isEditEnabled);

  React.useEffect(() => {
    if (user) {
      setEditable(!!user.isEditEnabled);
    }
  }, [user]);

  const [notifications, setNotifications] = React.useState<DashboardNotification[]>([]);

  const { materialsMap } = useMaterialsStore();
  const { pricesMap, convertionMap } = useCurrencyRates();

  const { refetch: fetchFutureOrders } = useFutureOrders();
  const { allFutureOrders } = useFutureOrderStore();
  const [isBalanceNowLoading, setIsBalanceNowLoading] = useState(false);
  const navigate = useNavigate();
  const { currentSelectedMaterialId, isNetExposureLoading, netExposure, setCurrentSelectedMaterialId } =
    useExposureStore();
  const { refetch: refetchExposure } = useExposure(account.id);

  const [terminals, setTerminals] = useState<string>(materialsMap[currentSelectedMaterialId]?.terminals || '');
  const [isTerminals, setIsTerminals] = useState<boolean>(false);
  const [isQuantity, setIsQuantity] = useState<boolean>(false);

  const setMaterial = (materialId: string) => {
    setCurrentSelectedMaterialId(materialId);

    setTerminals(materialId === GENERAL_ID ? '' : materialsMap[materialId]?.terminals);
    if (materialId === GENERAL_ID) {
      setIsQuantity(false);
    }
  };

  React.useEffect(() => {
    if (account?.id) {
      setMaterial(GENERAL_ID);

      refetchExposure();
      fetchInventory();
      fetchNotifications();
      fetchOldInventory();
      fetchFinPositions();
      fetchFutureOrders();
    }
  }, [account?.id]);

  if (account?.skipDashboard) {
    navigate(account.skipDashboard);
  }

  const [isRefetchingExposure, setIsRefetchingExposure] = useState(false);

  const handleUpdateExposure = async (accountId: string, materialId: string[]) => {
    setIsRefetchingExposure(true);
    try {
      await triggerExposureCalc({ accountId, materialIds: materialId });
    } catch (error) {
      console.error('Error updating exposure:', error);
      await new Promise((resolve) => setTimeout(resolve, 3000));
    }
    await refetchExposure();
    setIsRefetchingExposure(false);
  };

  const fetchNotifications = async () => {
    const notes = await getNotifications(account.id || '');

    setNotifications(notes || []);
  };

  const getDateModifiedYear = (newYear: number): Date => {
    const currentDate = new Date();
    currentDate.setFullYear(newYear);
    return currentDate;
  };

  const balanceNow = async (materialId: string) => {
    if (netExposure && netExposure.materialsExposure[materialId]) {
      setIsBalanceNowLoading(true);
      const balanceId = await balanceMaterial(
        materialId,
        account.id || '',
        getNetExposure(netExposure, convertionMap, materialId),
        netExposure.materialsExposure[materialId],
      );

      setIsBalanceNowLoading(false);
      if (balanceId) {
        navigate(`/balance-risk/${balanceId}`);
      }
    }
  };

  const memoAccumulatePositions = React.useMemo(
    () => mapPositions(financialPositions, currentSelectedMaterialId, materialsMap, colorsMap, pricesMap, true),
    [financialPositions, currentSelectedMaterialId],
  );

  const memoMonthlyPositions = React.useMemo(
    () => mapPositions(financialPositions, currentSelectedMaterialId, materialsMap, colorsMap, pricesMap, false),
    [financialPositions, currentSelectedMaterialId],
  );

  const memoNotifications = React.useMemo(
    () => [...(inventoryData?.notifications || []), ...notifications],
    [inventoryData, notifications],
  );

  const memoInvReports = React.useMemo(() => inventoryReports, [inventoryReports]);

  const memoFutureOrders = React.useMemo(
    () =>
      dictionaryToStackChart(
        aggregateFutureOrdersToMaterialsDic(allFutureOrders, undefined, pricesMap, false, isQuantity),
        colorsMap,
        materialsMap,
        selectedYear,
        pricesMap,
        materialsMap[currentSelectedMaterialId],
      ),
    [allFutureOrders, currentSelectedMaterialId, isQuantity, pricesMap, colorsMap, materialsMap, selectedYear, false],
  );

  const memoFutureOrdersAcumulated = React.useMemo(
    () =>
      dictionaryToStackChart(
        aggregateFutureOrdersToMaterialsDic(allFutureOrders, undefined, pricesMap, true, isQuantity),
        colorsMap,
        materialsMap,
        selectedYear,
        pricesMap,
        materialsMap[currentSelectedMaterialId],
      ),
    [allFutureOrders, currentSelectedMaterialId, isQuantity, pricesMap, colorsMap, materialsMap, selectedYear, true],
  );

  const memoFutOrdersNoQuant = React.useMemo(
    () =>
      dictionaryToStackChart(
        aggregateFutureOrdersToMaterialsDic(allFutureOrders, undefined, pricesMap, false, false),
        colorsMap,
        materialsMap,
        selectedYear,
        pricesMap,
        materialsMap[currentSelectedMaterialId],
      ),
    [allFutureOrders, currentSelectedMaterialId, pricesMap, colorsMap, materialsMap, selectedYear],
  );

  const memoNetExposure = React.useMemo(() => netExposure, [netExposure, currentSelectedMaterialId]);
  const [showExpDetails, setShowExpDetails] = useState(false);
  const [showSalesDetails, setShowSalesDetails] = useState(false);
  const [showInventoryDetails, setShowInventoryDetails] = useState(false);

  const [showAddSalesCommitment, setShowAddSalesCommitment] = useState(false);

  const showSalesPopup = () => {
    setShowSalesDetails(true);
  };

  const [maxValues, setMaxValues] = React.useState<{ futureOrder: number; inventory: number; position: number }>({
    futureOrder: 0,
    inventory: 0,
    position: 0,
  });
  const setInventoryMax = React.useCallback(
    (inventory: number) => {
      if (inventory !== maxValues.inventory) setMaxValues((prev) => ({ ...prev, inventory }));
    },
    [maxValues.inventory],
  );
  const setOrdersMax = React.useCallback(
    (futureOrder: number) => {
      if (futureOrder !== maxValues.futureOrder) setMaxValues((prev) => ({ ...prev, futureOrder }));
    },
    [maxValues.futureOrder],
  );
  const setPositionMax = React.useCallback(
    (position: number) => {
      if (position !== maxValues.position) setMaxValues((prev) => ({ ...prev, position }));
    },
    [maxValues.position],
  );

  const [chartMax, setChartMax] = React.useState<number>(0);
  const [showProportion, setShowProportion] = React.useState<boolean>(false);
  React.useEffect(() => {
    if (maxValues.futureOrder > 0 && maxValues.inventory > 0 && maxValues.position > 0) {
      const max = 1.02 * Math.max(maxValues.futureOrder, maxValues.inventory, maxValues.position, 1 / 1.02);

      setChartMax(max);
    }
  }, [maxValues.futureOrder, maxValues.inventory, maxValues.position]);

  return memoInvReports &&
    memoNetExposure &&
    memoAccumulatePositions &&
    memoFutureOrders &&
    memoFutureOrdersAcumulated &&
    memoNotifications ? (
    <div className="flex flex-col flex-1 h-full  gap-3">
      <MaterialTrendButton
        buttons={getMaterialsCardData(
          materialsMap,
          colorsMap,
          convertionMap,
          account.materialsToAccount,
          memoNetExposure,
        )}
        currentMaterial={currentSelectedMaterialId}
        setCurrentMaterial={setMaterial}
      />
      <div className="flex flex-row flex-1 space-x-4 overflow-hidden   ">
        <div className="flex basis-5/6 overflow-hidden">
          <div className="flex-col flex w-full align-start space-y-4  ">
            {memoNetExposure ? (
              <div>
                <ExposureCard
                  {...getExposureCardData(
                    memoNetExposure,
                    getMaterialToAccountById(account, currentSelectedMaterialId || 'general'),
                    account.riskPercents,
                    convertionMap,
                    materialsMap,
                    materialsMap[currentSelectedMaterialId],
                  )}
                  onBalanceNowClickAction={
                    editable
                      ? () =>
                          currentSelectedMaterialId === GENERAL_ID
                            ? navigate('./exposure-overview')
                            : balanceNow(currentSelectedMaterialId)
                      : undefined
                  }
                  showExposureDetails={['RFVCLLEUDC1', 'RFVCBLEUDC1'].includes(currentSelectedMaterialId)}
                  onExpDetailClk={() => setShowExpDetails(true)}
                  isBalanceNowLoading={isBalanceNowLoading}
                  isExposureLoading={isNetExposureLoading || isRefetchingExposure}
                  material={materialsMap[currentSelectedMaterialId]}
                />
              </div>
            ) : (
              <div className="flex flex-col grow w-full min-h-36">
                <div className="flex flex-grow animate-pulse bg-white"></div>
              </div>
            )}
            <div className=" flex flex-row  h-full  space-x-6 pb-2  justify-between overflow-hidden ">
              <div className=" min-w-[10rem] shadow-md flex-1  bg-white overflow-auto ">
                {memoFutureOrders && memoFutureOrdersAcumulated ? (
                  <StackedBarDoughnutChartCard
                    data={memoFutureOrders}
                    cardTitle={account.titleOverrides?.['futureOrders'] || 'Sales commitments'}
                    accumulated={memoFutureOrdersAcumulated}
                    cardDescription="Sales commitments"
                    cardUrl={account.isSalesCommitment ? '/sales-commitments' : '/future-order'}
                    addNewUrl={
                      editable && !account.isSalesCommitment
                        ? `/${ADD_FUTURE_ORDER_ROOT_ROUTE}/${ADD_FUTURE_ORDER_DEFAULT_ORDER_ID}/${ADD_FO_FIRST_STEP}`
                        : undefined
                    }
                    onAddNewClick={
                      editable && account.isSalesCommitment ? () => setShowAddSalesCommitment(true) : undefined
                    }
                    currentDate={getDateModifiedYear(selectedYear)}
                    materialId={currentSelectedMaterialId}
                    terminals="" // we want dashboard to be disconnected from terminals
                    {...{
                      setIsTerminals,
                      isTerminals,
                      showDetails: showSalesPopup,
                      isQuantity,
                      setIsQuantity,
                      setMaxVal: setOrdersMax,
                      chartMax,
                      showProportion,
                    }}
                  />
                ) : (
                  <div className="flex w-full h-full bg-gray-100 opacity-20 animate-pulse"></div>
                )}
              </div>
              <div className="min-w-[10rem] flex-1 h-full    bg-white shadow-md overflow-auto ">
                <InventoryChartCard
                  inventoryReports={memoInvReports}
                  selectedMaterial={currentSelectedMaterialId}
                  selectedYear={selectedYear}
                  colorsMap={colorsMap}
                  refetchData={handleUpdateExposure}
                  showDetails={() => setShowInventoryDetails(true)}
                  terminals=""
                  {...{ setIsTerminals, isTerminals, editable, setIsQuantity, isQuantity }}
                  {...{ setMaxVal: setInventoryMax, chartMax, showProportion }}
                ></InventoryChartCard>
              </div>

              <div className=" min-w-[10rem]  flex-1 bg-white shadow-md overflow-auto ">
                <StackedBarDoughnutChartCard
                  isFinancialPositions
                  data={memoMonthlyPositions}
                  accumulated={memoAccumulatePositions} //TODO: change the data to month based
                  cardTitle="Financial positions"
                  cardDescription="Financial positions"
                  cardUrl="/financial-positions"
                  currentDate={getDateModifiedYear(selectedYear)}
                  materialId={currentSelectedMaterialId}
                  terminals=""
                  {...{ setIsTerminals, isTerminals, isQuantity, setIsQuantity }}
                  {...{ setMaxVal: setPositionMax, chartMax, showProportion }}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-1 flex-col flex-grow pb-2   basis-1/6 ">
          <div className="flex flex-1 flex-col h-full gap-4  ">
            <div className="h-full">
              <NotificationDashboardCard
                currentMaterialId={currentSelectedMaterialId}
                accountId={account.id}
                colorsToMaterial={colorsMap}
                notifications={memoNotifications}
                colorsMap={colorsMap}
                isLoading={isBalanceNowLoading}
                refetchNotifications={fetchNotifications}
                editEnabled={editable}
                setProportion={(isProp: boolean) => setShowProportion(isProp)}
              />
            </div>
          </div>
        </div>
      </div>
      {showExpDetails && (
        <ExposureDetailsPopup
          close={() => setShowExpDetails(false)}
          materialId={currentSelectedMaterialId}
          orders={memoFutOrdersNoQuant}
          inventory={memoInvReports}
          positions={memoAccumulatePositions}
        />
      )}
      {showSalesDetails && (
        <OverlayDisplayDiv setDisplayOverlay={() => setShowSalesDetails(false)}>
          {memoFutureOrders ? (
            <StackedBarChartDetails
              monthExposures={memoFutureOrders}
              cumulativeExposures={memoFutureOrdersAcumulated}
              cardTitle={account.titleOverrides?.['futureOrders'] || 'Sales commitments'}
              cardDescription="Sales commitments"
              cardUrl="/future-order"
              addNewUrl={`/${ADD_FUTURE_ORDER_ROOT_ROUTE}/${ADD_FUTURE_ORDER_DEFAULT_ORDER_ID}/${ADD_FO_FIRST_STEP}`}
              currentDate={getDateModifiedYear(selectedYear)}
              materialId={currentSelectedMaterialId}
              {...{ setIsTerminals, isTerminals, terminals, isQuantity, setIsQuantity }}
            />
          ) : (
            <div className="flex w-full h-full bg-gray-100 opacity-20 animate-pulse"></div>
          )}
        </OverlayDisplayDiv>
      )}
      {showInventoryDetails && (
        <OverlayDisplayDiv setDisplayOverlay={() => setShowInventoryDetails(false)}>
          <InventoryDetails
            inventoryReports={memoInvReports}
            selectedMaterial={currentSelectedMaterialId}
            selectedYear={selectedYear}
            colorsMap={colorsMap}
            refetchData={handleUpdateExposure}
            showDetails={() => setShowInventoryDetails(true)}
            {...{ setIsTerminals, isTerminals, terminals, editable, setIsQuantity, isQuantity }}
            close={() => setShowInventoryDetails(false)}
            isPopup
            {...{ setMaxVal: () => 0, chartMax: 0, showProportion: false }}
          />
        </OverlayDisplayDiv>
      )}
      {showAddSalesCommitment && (
        <OverlayDisplayDiv setDisplayOverlay={() => setShowAddSalesCommitment(false)} hideClose>
          <AddSalesCommitmentPopup
            cancel={() => setShowAddSalesCommitment(false)}
            onCommitmentAdded={() => {
              setShowAddSalesCommitment(false);
              fetchFutureOrders();
            }}
          />
        </OverlayDisplayDiv>
      )}
    </div>
  ) : (
    <SkeletonDashboard />
  );
}
